import React, { useEffect, useMemo } from 'react';
import { FormikHelpers, useFormik } from 'formik';
import { Placeholder } from 'rsuite';
import { MutationFunction, useMutation } from '@apollo/client';
import { Trans, useTranslation } from 'react-i18next';
import {
	alephToast,
	Button,
	ButtonAppearance, ButtonColor,
	CheckIcon,
	Floaty,
	Input,
	LoaderBars,
	NotificationToast,
	NotificationType,
	ButtonSize,
	ComboBox,
	isReddit,
	isSpotify,
	getNewAvatar,
	getRandomMainColor,
	AvatarEntity,
	IMenuOption,
	MenuSelectType,
} from '@aleph/shared-components';
import useRedditUserMutationStorage, {
	newAuthGraphUserData,
	newUserData,
} from 'wb-reddit/app/routes/users/mutator/useRedditUserMutationStorage';
import {
	CreateRedditUserDocument,
	CreateRedditUserMutation,
	CreateRedditUserMutationVariables,
	CreateUserAccountLevel,
	CreateUserMutation,
	InviteUserToGroupsDocument,
	UserAccountLevel,
} from 'wb-reddit/graphql/generated';
import {
	canUserEditGroup,
	getCreateUserValidationSchema,
	MutatingRedditUser,
	UserGroupOption,
} from 'wb-reddit/app/routes/users/mutator/utils';
import ProfileImageUploader from 'wb-reddit/app/common/components/profileImageUploader';
import GroupListItem from 'wb-reddit/app/routes/users/components/groupListItem';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import AccessBadge from 'wb-reddit/app/common/components/accessBadge';
import { getRedditUserLevelName } from 'wb-reddit/app/routes/users/utils';
import { MutatorProps } from 'wb-reddit/app/routes/users/mutator/container';
import UsersTeamsAutocomplete from 'wb-reddit/app/routes/users/mutator/usersTeamsAutocomplete';
import {
	AuthGraphCreateUserDocument,
	AuthGraphCreateUserMutation,
	AuthGraphCreateUserMutationVariables,
	UserAccountLevel as AuthGraphUserAccountLevel,
	AuthGraphAddUserToUserGroupDocument,
	AuthGraphAddUserToUserGroupMutation,
	AuthGraphAddUserToUserGroupMutationVariables,
} from 'wb-spotify/graphql/authgraph/generated';
import { UserRow } from 'packages/wb-reddit/src/app/routes/users/container';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';
import { AuthGraphAssignCredentialsDocument, WbnPlatform } from 'wb-reddit/graphql/authgraph/generated';

// Taken from authgraph
const ALLOWED_CREATE_ROLES_PER_PLATFORM_USER_LEVEL = {
	[AuthGraphUserAccountLevel.PlatformAdmin]:   [AuthGraphUserAccountLevel.User, AuthGraphUserAccountLevel.AdvertiserAdmin, AuthGraphUserAccountLevel.AgencyAdmin, AuthGraphUserAccountLevel.PlatformAdmin],
	[AuthGraphUserAccountLevel.AgencyAdmin]:     [AuthGraphUserAccountLevel.User, AuthGraphUserAccountLevel.AdvertiserAdmin, AuthGraphUserAccountLevel.AgencyAdmin],
	[AuthGraphUserAccountLevel.AdvertiserAdmin]: [AuthGraphUserAccountLevel.User],
	[AuthGraphUserAccountLevel.User]:            [],
};

const RedditUsersMutator = ({ updateRow, setShouldShowSaveBeforeExit, beforeClose }: MutatorProps) => {
	const { t } = useTranslation();
	const { mutationUser, setMutationUser } = useRedditUserMutationStorage();

	const [assignCredentials, { loading: assigningCredentials }] = useMutation(AuthGraphAssignCredentialsDocument);
	const [onCreateUser, { loading: creating }] = useMutation(isReddit()
		? CreateRedditUserDocument
		: AuthGraphCreateUserDocument, {
		errorPolicy: 'all',
		onCompleted: (user, clientOptions) => {
			if (isReddit()) {
				updateRow?.({
					// @ts-ignore: issue with userlevel enum difference between authgraph and reddit.
					// TODO: fix type issue
					row:      user.createUser,
					isCreate: true,
				});
				clientOptions?.client?.resetStore();
			}
		},
		awaitRefetchQueries: true,
	});

	const [inviteUserToGroups, { loading: invitingUser }] = useMutation(
		isReddit()
			? InviteUserToGroupsDocument
			: AuthGraphAddUserToUserGroupDocument,
		{
			errorPolicy: 'all',
			onError() {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={t('re: user creation failed to invite to group', 'Failed to invite to group')}
					/>,
				);
			},
		},
	);

	const currentPlatformUser = useUserMetaData();

	const onConfirmMutation = async (values: MutatingRedditUser, { setErrors }: FormikHelpers<MutatingRedditUser>) => {
		if (isReddit()) {
			const onCreatedRedditUser = onCreateUser as MutationFunction<CreateRedditUserMutation, CreateRedditUserMutationVariables>;

			const responseData = await onCreatedRedditUser({
				variables: {
					userName:           values?.userName ?? '',
					userSurname:        values?.userSurname ?? '',
					userEmail:          values?.userEmail ?? '',
					userAccountLevel:   values?.userAccountLevel ?? CreateUserAccountLevel.User,
					userProfilePicture: values?.userProfilePicture?.file ?? undefined,
				},
			});

			if (
				(values?.userGroups ?? [])?.length > 0
				&& (responseData?.data as CreateUserMutation)?.createUser?.userId
			) {
				await inviteUserToGroups({
					variables: {
						userId:       (responseData?.data as CreateUserMutation)?.createUser?.userId ?? '',
						// @ts-ignore
						userGroupIds: values?.userGroups?.map((curr) => curr.value) ?? [],
					},
				});
			}

			// @ts-ignore
			const validationErrors = Object.keys(responseData.errors?.[0]?.form ?? {}).reduce((accu, field) => {
				// @ts-ignore
				accu[field] = t(responseData.errors?.[0]?.form?.[field]?.message ?? t('sc: unknown err', 'Unknown error'));

				return accu;
			}, {});

			setErrors(validationErrors);

			if (!Object.keys(validationErrors).length) {
				if (responseData.errors) {
					alephToast(
						<NotificationToast
							type={NotificationType.Error}
							title={
								<div className="text-white">
									{t('re: user creation failed to create user', 'Failed to create user')}
								</div>
							}
							description={t(responseData?.errors?.[0]?.message ?? 'Unknown error')}
						/>,
						{ position: 'bottom-left' },
					);

					return;
				}

				alephToast(
					<NotificationToast
						type={NotificationType.Success}
						title={
							<div className="text-white">
								<Trans
									i18nKey="re: user creation success"
									defaults="User <strong>{{userName}}</strong> successfully created."
									values={{
										userName: values.userName,
									}}
								>
									User <strong>{values.userName}</strong> successfully created.
								</Trans>
							</div>
						}
					/>,
					{ position: 'bottom-left' },
				);
				setMutationUser(undefined);
			}

			return;
		}

		const onCreateAuthGraphUser = onCreateUser as MutationFunction<AuthGraphCreateUserMutation, AuthGraphCreateUserMutationVariables>;

		const responseData = await onCreateAuthGraphUser({
			variables: {
				userName:         values?.userName ?? '',
				userSurname:      values?.userSurname ?? '',
				userEmail:        values?.userEmail ?? '',
				// @ts-ignore
				userAccountLevel: values?.userAccountLevel ?? AuthGraphUserAccountLevel.User,
				userPassword:     values?.userPassword ?? '',
			},
		});

		const userGroups = [
			...(responseData?.data?.createUser?.userGroupAccess ?? [])
				.filter(group => group?.userGroup?.userGroupName)
				.map(
					group => ({
						...group.userGroup,
						userGroupName:       group.userGroup?.userGroupName as string,
						userGroupAccessRole: group.userGroupAccessRole,
					}),
				),
		];

		if (
			userGroups.length
			&& (responseData?.data as CreateUserMutation)?.createUser?.userId
		) {
			const inviteUserToGroupsMutation = inviteUserToGroups as MutationFunction<AuthGraphAddUserToUserGroupMutation, AuthGraphAddUserToUserGroupMutationVariables>;

			// TODO: This should be done in the backend, so same as on reddit.
			// We pass in array of group ids and user gets invited to those groups
			// Execute invitations sequentially instead of using Promise.all
			// @ts-ignore: We already check the length of the userGroups array in the if above
			// eslint-disable-next-line no-restricted-syntax
			for (const group of values.userGroups) {
				// eslint-disable-next-line no-await-in-loop
				const { data, errors } = await inviteUserToGroupsMutation({
					variables: {
						userId:      (responseData?.data as CreateUserMutation)?.createUser?.userId ?? '',
						userGroupId: group.value,
					},
				});

				if (!errors && data?.addUserToUserGroup?.userGroup?.userGroupName) {
					userGroups.push({
						...data.addUserToUserGroup.userGroup,
						userGroupName:       data.addUserToUserGroup.userGroup.userGroupName as string,
						// @ts-ignore: trust me;
						// this field is added in packages/wb-reddit/src/app/routes/users/container.tsx getRows
						userGroupAccessRole: data?.addUserToUserGroup?.userGroupAccessRole,
					});
				}
			}
		}

		// @ts-ignore
		const validationErrors = Object.keys(responseData.errors?.[0]?.form ?? {}).reduce((accu, field) => {
			// @ts-ignore
			accu[field] = t(responseData.errors?.[0]?.form?.[field]?.message ?? t('sc: unknown err', 'Unknown error'));

			return accu;
		}, {});

		setErrors(validationErrors);

		if (!Object.keys(validationErrors).length) {
			if (responseData.errors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={
							<div className="text-white">
								{t('re: user creation failed to create user', 'Failed to create user')}
							</div>
						}
						description={t(responseData?.errors?.[0]?.message ?? 'Unknown error')}
					/>,
					{ position: 'bottom-left' },
				);

				return;
			}

			await assignCredentials({
				variables: {
					userId:      responseData?.data?.createUser?.userId as string,
					WBNPlatform: WbnPlatform.Spotify,
				},
			});

			updateRow?.({
				// @ts-ignore
				row: {
					...responseData?.data?.createUser,
					userId:             responseData?.data?.createUser?.userId as string,
					id:                 responseData?.data?.createUser?.id as string,
					userEmail:          responseData?.data?.createUser?.userEmail as string,
					userName:           responseData?.data?.createUser?.userName as string,
					userSurname:        responseData?.data?.createUser?.userSurname as string,
					userProfilePicture: null,
					// @ts-ignore
					userGroups,
					// Typecast to UserAccountLevel because the difference is in enums, should be fine
					userAccountLevel:   responseData?.data?.createUser?.userAccountLevel as unknown as UserAccountLevel,
				} satisfies UserRow,
				isCreate: true,
			});

			setMutationUser(undefined);

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={
						<div className="text-white">
							<Trans
								i18nKey="re: user creation success"
								defaults="User <strong>{{userName}}</strong> successfully created."
								values={{
									userName: values.userName,
								}}
							>
								User <strong>{values.userName}</strong> successfully created.
							</Trans>
						</div>
					}
				/>,
				{ position: 'bottom-left' },
			);
		}
	};

	const {
		values,
		handleChange,
		handleBlur,
		errors,
		touched,
		submitForm,
		setFieldValue,
		setFieldTouched,
		dirty,
	} = useFormik({
		// @ts-ignore
		initialValues: mutationUser ?? (isReddit()
			? newUserData
			: newAuthGraphUserData),
		initialTouched: isReddit()
			? {
				userProfilePicture: !!(mutationUser as MutatingRedditUser)?.userProfilePicture,
			}
			: undefined,
		validationSchema: getCreateUserValidationSchema(currentPlatformUser),
		onSubmit:         onConfirmMutation,
	});

	useEffect(() => {
		setMutationUser(values);
	}, [values]);

	useEffect(() => {
		setFieldValue('userGroups', []);
	}, [values.userEmail]);

	useEffect(() => {
		setFieldValue('userProfilePicture', undefined);
	}, []);

	useEffect(
		() => setShouldShowSaveBeforeExit(dirty),
		[dirty],
	);

	const roleOptions = useMemo(() => {
		if (isReddit()) {
			return Object.values(UserAccountLevel)
				.map((level) => ({
					label: getRedditUserLevelName(level),
					value: level,
				}))
				// Exclude the 'Unknown' account level
				.filter(({ value }) => value !== UserAccountLevel.Unknown)
				// Only allow Admin users to create Admin users
				.filter(
					({ value }) => value !== UserAccountLevel.Admin
						|| currentPlatformUser?.userAccountLevel === UserAccountLevel.Admin,
				);
		}
		const options = Object.values(ALLOWED_CREATE_ROLES_PER_PLATFORM_USER_LEVEL[currentPlatformUser?.userAccountLevel as unknown as AuthGraphUserAccountLevel]);

		return (
			options.map((level) => ({
				label: getRedditUserLevelName(level),
				value: level,
			}))
		);
	}, []);

	const anyInvalidTouched = () => Object.keys(touched).some((key) => touched[key] && errors[key]);

	const onUploadProfileImage = (newImage) => {
		setFieldValue('userProfilePicture', newImage);

		setFieldTouched('userProfilePicture');
	};

	const nameInitials = `${values.userName?.trim()?.[0] ?? ''}${values.userSurname?.trim()?.[0] ?? ''}`.toUpperCase();
	const imageColor = useMemo(() => getRandomMainColor(), []);

	useEffect(() => {
		if (!touched.userProfilePicture) {
			getNewAvatar({ entityType: 'User', background: imageColor, initials: nameInitials })
				.then((newImage) => {
					setFieldValue('userProfilePicture', newImage);
				});
		}
	}, [nameInitials]);

	const onInviteUserToGroup = async (option: UserGroupOption) => {
		setFieldValue('userGroups', [...(values.userGroups ?? []), option]);
	};

	const revokeUserGroup = (group: UserGroupOption) => {
		setFieldValue(
			'userGroups',
			values.userGroups?.filter((currGroup) => currGroup.value !== group.value) ?? [],
		);
	};

	const showDiscard = (
		touched.userName
		|| touched.userSurname
	) && dirty;

	const { canCreateUser: spotifyCanCreateUser } = useSpotifyRolePermissions();
	const platformUser = useUserMetaData();
	const canEditUser = useMemo(
		() => {
			if (isReddit()) {
				return platformUser?.isAdmin;
			}

			return spotifyCanCreateUser;
		},
		[platformUser, spotifyCanCreateUser],
	);

	const isCreatingUser = creating || assigningCredentials;
	const confirmButtonDisabled = isCreatingUser || anyInvalidTouched() || invitingUser;

	return (
		<div className="flex flex-1 min-w-full px-[40px] pb-0 pt-[40px] gap-[40px]">
			<div className="flex-1 w-[25%] flex justify-center">
				<ProfileImageUploader
					profileImage={values.userProfilePicture}
					onUploadProfileImage={onUploadProfileImage}
					type="User"
					readOnly={!canEditUser}
					disabled={isSpotify()}
					avatarEntity={AvatarEntity.User}
				/>
			</div>
			<div className="flex-[2] min-w-[50%] w-[50%] flex flex-col gap-[24px]
					px-[2px] pb-[150px]"
			>
				<div className="font-semibold text-[14px]">
					{t('re: user general information', 'GENERAL INFORMATION')}
				</div>
				<div className="flex gap-[16px]">
					<Input
						className="flex-1"
						id="userName"
						label={t('re:user first name', 'First name')}
						onChange={handleChange}
						value={values.userName ?? ''}
						validationError={touched.userName ? errors.userName : undefined}
						onBlur={handleBlur}
						disabled={!canEditUser}
					/>

					<Input
						className="flex-1"
						id="userSurname"
						label={t('re:user last name', 'Last name')}
						onChange={handleChange}
						value={values?.userSurname ?? ''}
						validationError={touched.userSurname ? errors.userSurname : undefined}
						onBlur={handleBlur}
						disabled={!canEditUser}
					/>
				</div>

				<div className="flex gap-[16px]">
					<Input
						className="flex-1"
						id="userEmail"
						type="email"
						label={t('re: user email', 'E-mail')}
						disabled={!canEditUser}
						onChange={handleChange}
						value={values?.userEmail ?? ''}
						validationError={(touched.userEmail || touched.userEmailConfirmation)
							? errors.userEmail as string
							: undefined}
						onBlur={handleBlur}
					/>

					<Input
						className="flex-1"
						id="userEmailConfirmation"
						type="email"
						label={t('re: user creation email confirmation', 'Repeat e-mail')}
						onChange={handleChange}
						disabled={!canEditUser}
						value={values?.userEmailConfirmation ?? ''}
						validationError={(touched.userEmail || touched.userEmailConfirmation)
							? errors.userEmailConfirmation as string
							: undefined}
						onBlur={handleBlur}
					/>
				</div>

				{isSpotify() && (
					<div className="flex gap-[16px]">
						<Input
							className="flex-1"
							id="userPassword"
							label={t('sp: user password', 'Password')}
							onChange={handleChange}
							value={values.userPassword ?? ''}
							validationError={touched.userPassword ? errors.userPassword : undefined}
							onBlur={handleBlur}
							type="password"
						/>

						<Input
							className="flex-1"
							id="userPasswordConfirmation"
							label={t('sp: user password confirmation', 'Password confirmation')}
							onChange={handleChange}
							value={values?.userPasswordConfirmation ?? ''}
							validationError={touched.userPasswordConfirmation ? errors.userPasswordConfirmation : undefined}
							onBlur={handleBlur}
							type="password"
						/>
					</div>
				)
				}

				<div className="font-semibold text-[14px] pt-[16px]">
					{t('re: user permissions', 'PERMISSIONS')}
				</div>

				<div>
					{/* @ts-ignore: TODO: type issue in combobox */}
					<ComboBox
						value={values?.userAccountLevel
							? getRedditUserLevelName(values?.userAccountLevel) : ''}
						disabled={!canEditUser || !roleOptions.length}
						size={ButtonSize.MEDIUM}
						triggerWrapperClassName="w-full"
						label={t('re: user role', 'Role')}
						pulldown
						items={roleOptions}
						selectType={MenuSelectType.SINGLE_SELECT}
						onSelect={(option) => {
							setFieldValue('userAccountLevel', (option as IMenuOption)?.value);
						}}
						openMenuOnClick
					/>
					{
						errors?.userAccountLevel && touched?.userAccountLevel
						&& <div className="text-red-500 text-[10px]">{errors.userAccountLevel}</div>
					}
				</div>

				<div className="font-semibold text-[14px]">
					{t('re: user teams', 'TEAMS')}
				</div>

				<UsersTeamsAutocomplete
					disabled={!!errors.userEmail || !canEditUser}
					onOptionClick={onInviteUserToGroup}
					values={values}
				/>

				<div>
					<div className="flex border-b text-[11px] border-gray-200 px-[8px] h-[32px] items-center
							text-gray-600"
					>
						<div className="flex-1">
							{t('re: user team', 'TEAM')}
						</div>
						<div className="w-[60px] flex justify-center">
							{t('re: user revoke', 'REVOKE')}
						</div>
					</div>
					{
						values.userGroups?.map((group) => (
							<GroupListItem
								group={group}
								user={mutationUser}
								onRevokeGroup={revokeUserGroup}
								hideActions={
									!isSpotify() && (
										!canUserEditGroup(group, currentPlatformUser)
										&& currentPlatformUser?.userAccountLevel !== UserAccountLevel.Admin
									)
								}
							/>
						))
					}

					{
						invitingUser
							&& <div className="flex h-[48px] px-[8px] gap-[8px] items-center justify-center">
								<Placeholder.Graph
									active
									className="!h-[32px] !w-[32px] min-w-[32px] !rounded-full"
								/>

								<Placeholder.Graph
									active
									className="!h-[16px]"
								/>
							</div>
					}
				</div>
			</div>
			<div className="flex-1 w-[25%] flex flex-col gap-[16px]">
				<div className="font-semibold text-[14px]">
					{t('re: user sidebar wise blue', 'WISE.BLUE')}
				</div>

				<div className="flex text-[12px]">
					<div className="w-[80px] flex flex-col gap-[8px] text-gray-600 font-semibold">
						<div>{t('re: user sidebar id', 'ID')}</div>
						<div>{t('re: user sidebar access', 'Access')}</div>
					</div>
					<div className="text-black flex flex-col gap-[8px]">
						<div className="text-gray-800">
							{values.userId ?? '-'}
						</div>
						<div>
							{
								values?.userId ? <AccessBadge
									// @ts-ignore
									accessEntities={mutationUser?.userAccess ?? []}
								/> : '-'
							}
						</div>
					</div>
				</div>
			</div>

			{
				canEditUser
				&& <Floaty>
					{
						!creating
							&& showDiscard
							&& <Button
								title={t('sc: discard', 'Discard')}
								data-testid="users-discard"
								appearance={ButtonAppearance.OUTLINED}
								color={ButtonColor.RED}
								onClick={beforeClose}
							/>
					}

					<Button
						title={t('re: create user', 'Create user')}
						data-testid="users-confirm"
						leftNode={
							isCreatingUser
								? <LoaderBars className="animate-spin"/>
								: <CheckIcon />
						}
						disabled={confirmButtonDisabled}
						onClick={submitForm}
						color={ButtonColor.GREEN}
					/>
				</Floaty>
			}
		</div>
	);
};

export default RedditUsersMutator;
